import React, { useRef, useEffect, useState, useContext } from 'react'
import useSize from './useSize'
import { scaleLinear } from 'd3-scale'
import { line, curveBasis } from 'd3-shape'
import {
  HorizontalTicks,
  VerticalTicks,
  AxisLabel,
  Curve
} from 'components/charts/ChartElements'

import './styles.scss'

const f = new Intl.NumberFormat('de-CH')

type ChartProps = {
  data: namedataElement[]
  selected: namedataElement[]
  colors: string[]
}
const yProp = 'abs'

export const Chart: React.FC<ChartProps> = ({ data, selected, colors }) => {
  const [yDomain, setYDomain] = useState<number[]>([0, 1])

  useEffect(() => {
    if (selected.length <= 0) return
    const max =
      Math.ceil(
        Math.max.apply(
          null,
          selected.map(s => {
            return Math.max.apply(
              null,
              s.years.map(y => y[yProp])
            )
          })
        ) / 0.2
      ) * 0.2
    setYDomain([0, max + max * 0.1])
  }, [selected])

  const [container, size] = useSize()
  const width = size.width
  const height = size.isMobile ? 400 : 550
  const gutterTop = 20
  const gutterRight = 5
  const gutterLeft = 5
  const gutterBottom = 25

  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain([1960, 2022])
        .range([plotArea.left, plotArea.right]),
    [size, yDomain, selected]
  )
  const yAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(yDomain)
        .range([plotArea.bottom, plotArea.top]),
    [size, yDomain, selected]
  )

  const yFunction = (d: nameyearElement) => yAxisScale(d.abs)
  const xFunction = (d: nameyearElement) => xAxisScale(d.year)
  const svg = useRef<SVGSVGElement>(null)

  const lineFunction = React.useMemo(
    () =>
      line<nameyearElement>()
        .x(xFunction)
        .y(yFunction)
        .curve(curveBasis),
    [size, yDomain, selected]
  )

  const yTicks = [0, yDomain[1] / 2, yDomain[1]]
  const xTicks = size.isMobile ? [1985, 2000, 2015] : [1970, 1985, 2000, 2015]

  const o =
    xAxisScale(1960 + (2020 - 1960) / 2) - xAxisScale(selected[0].infos[2])

  return (
    <div className='namechart'>
      <div ref={container} style={{ position: 'relative', width: '100%' }}>
        <div
          className='culture-tooltip'
          style={{
            left: `${xAxisScale(selected[0].infos[2])}px`,
            marginLeft:
              o < 0 ? (window.innerWidth > 599 ? '-305px' : '-155px') : '5px',
            top: '60px'
          }}
        >
          <div>
            <b>{selected[0].infos[2]}</b>
          </div>
          <div>{selected[0].infos[1]}</div>
        </div>
        <div
          className='chart-head'
          style={{
            top: `${yAxisScale(
              selected[0].years[selected[0].years.length - 1].abs
            )}px`,
            left: `${xAxisScale(2020)}px`,
            backgroundImage:
              'url(' +
              require(`../${selected[0].infos[0]
                .replace('é', 'e')
                .toLocaleLowerCase()}.png`) +
              ')'
          }}
        />
        <svg
          id='namechart'
          className='namechart'
          ref={svg}
          width={width}
          height={height}
          style={{ backgroundColor: 'transparent' }}
        >
          <rect
            x={gutterLeft}
            y={gutterTop}
            width={size.width - gutterLeft - gutterRight}
            height={height - gutterTop - gutterBottom}
            fill='var(--background-color-light)'
            pointerEvents='none'
          />
          <AxisLabel
            text='Anzahl Personen mit diesem Namen'
            dir='up'
            pos={[0, yAxisScale.range()[1] - 20]}
          />
          <AxisLabel
            text='Jahrgang'
            dir='right'
            pos={[0, yAxisScale.range()[0] + 10]}
          />

          <HorizontalTicks
            values={yTicks}
            x={xAxisScale}
            y={yAxisScale}
            format={(m: number) => {
              return Math.round(m)
            }}
            textAnchor='start'
          />
          <VerticalTicks
            values={xTicks}
            x={xAxisScale}
            y={yAxisScale}
            format={(m: number) => {
              return m.toString()
            }}
          />
          {selected &&
            selected.map((m, i) => {
              return (
                <Curve
                  key={i}
                  d={
                    lineFunction(
                      m.years.filter(f => f.year >= xAxisScale.domain()[0])
                    ) || ''
                  }
                  color={colors[1]}
                />
              )
            })}

          {selected && (
            <line
              x1={xAxisScale(selected[0].infos[2])}
              x2={xAxisScale(selected[0].infos[2])}
              y1={yAxisScale.range()[0]}
              y2={yAxisScale.range()[1]}
              className='yearline'
            />
          )}
        </svg>
      </div>
    </div>
  )
}
